import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   Contacts.js — redesigned light theme + fixed scanner
   BUG FIXES:
   1. jsQR loaded via useEffect script injection (not <script> in JSX which
      is ignored by React and never executes)
   2. Scanner modal doesn't try to load jsQR until it's ready
   3. Camera cleanup on unmount properly stops all tracks
   4. extractFlapId handles more URL patterns
───────────────────────────────────────────────────────────────────────────── */

const B = '#1c73ba';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
:root{--blue:#1c73ba;--bl:#e8f3fb;--bd:#155fa0;--text:#111827;--muted:#6b7280;--border:#e5e7eb;--bg:#f3f4f6;--red:#ef4444;--green:#10b981;--gold:#f59e0b;--purple:#7c3aed;--amber:#f59e0b}
*{box-sizing:border-box;margin:0;padding:0}
.ct-root{min-height:100vh;background:var(--bg);font-family:'Inter',system-ui,sans-serif;color:var(--text);-webkit-font-smoothing:antialiased}
.ct-nav{position:sticky;top:0;z-index:50;background:#fff;border-bottom:1px solid var(--border);padding:0 20px;height:56px;display:flex;align-items:center;gap:10px}
.ct-logo{font-size:1.1rem;font-weight:700;color:var(--blue);text-decoration:none;margin-right:auto}
.ct-logo span{color:var(--gold)}
.ic-btn{width:34px;height:34px;border:1px solid var(--border);border-radius:8px;background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--muted);transition:all .15s;flex-shrink:0}
.ic-btn:hover{border-color:var(--blue);color:var(--blue);background:var(--bl)}
.ct-body{max-width:680px;margin:0 auto;padding:0 16px}
.ct-search-wrap{padding:14px 0;position:sticky;top:56px;z-index:10;background:var(--bg)}
.ct-search-inner{background:#fff;border:1.5px solid var(--border);border-radius:10px;display:flex;align-items:center;padding:0 12px;gap:8px;transition:border .15s}
.ct-search-inner:focus-within{border-color:var(--blue)}
.ct-search{flex:1;border:none;outline:none;font-size:14px;color:var(--text);padding:10px 0;background:transparent;font-family:inherit}
.ct-scan-btn{display:flex;align-items:center;gap:6px;padding:7px 14px;border:none;border-radius:8px;background:var(--blue);color:#fff;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;transition:background .15s;white-space:nowrap;flex-shrink:0}
.ct-scan-btn:hover{background:var(--bd)}
.ct-filters{display:flex;gap:6px;padding:0 0 12px;overflow-x:auto;-ms-overflow-style:none;scrollbar-width:none}
.ct-filters::-webkit-scrollbar{display:none}
.ct-filter{padding:5px 13px;border-radius:99px;border:1px solid var(--border);background:#fff;font-size:12px;font-weight:500;color:var(--muted);cursor:pointer;font-family:inherit;transition:all .15s;white-space:nowrap;flex-shrink:0}
.ct-filter:hover{border-color:var(--blue);color:var(--blue)}
.ct-filter.sel{background:var(--bl);border-color:var(--blue);color:var(--blue);font-weight:700}
.ct-group-label{font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:1px;padding:14px 0 6px}
.ct-contact{display:flex;align-items:center;gap:12px;padding:12px 0;border-bottom:1px solid #f9fafb;cursor:pointer;transition:all .15s;border-radius:4px}
.ct-contact:hover{background:#f9fafb}
.ct-contact-name{font-size:14px;font-weight:600;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin-bottom:2px}
.ct-contact-sub{font-size:12px;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ct-badge{font-size:10px;font-weight:700;padding:2px 7px;border-radius:99px;text-transform:uppercase;letter-spacing:.5px;flex-shrink:0}
.ct-empty{text-align:center;padding:60px 20px;color:var(--muted)}
.ct-spinner{width:18px;height:18px;border:2px solid var(--bl);border-top-color:var(--blue);border-radius:50%;animation:spin .7s linear infinite;display:inline-block}
.ct-center{min-height:60px;display:flex;align-items:center;justify-content:center}
@keyframes spin{to{transform:rotate(360deg)}}
/* Modal */
.ct-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px)}
.ct-modal{background:#fff;border-radius:16px;width:min(480px,96vw);max-height:88vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,0.18);display:flex;flex-direction:column}
.ct-modal-head{padding:18px 20px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
.ct-modal-title{font-size:15px;font-weight:700;color:var(--text)}
.ct-modal-body{padding:18px 20px;flex:1}
/* Scanner */
.ct-scanner-frame{border-radius:14px;overflow:hidden;position:relative;background:#000;aspect-ratio:1}
.ct-scanner-corner{position:absolute;width:24px;height:24px}
.ct-scanner-line{position:absolute;left:10%;right:10%;height:2px;background:linear-gradient(90deg,transparent,var(--blue),transparent);animation:scanLine 2s linear infinite}
@keyframes scanLine{0%{top:15%}50%{top:80%}100%{top:15%}}
.ct-field{margin-bottom:14px}
.ct-label{display:block;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.8px;margin-bottom:5px}
.ct-input{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:8px;font-size:13px;color:var(--text);outline:none;font-family:inherit;transition:border .15s;background:#fff}
.ct-input:focus{border-color:var(--blue)}
.ct-textarea{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:8px;font-size:13px;color:var(--text);outline:none;resize:vertical;font-family:inherit;min-height:70px;transition:border .15s}
.ct-textarea:focus{border-color:var(--blue)}
.ct-row{display:flex;align-items:flex-start;gap:10px;margin-bottom:12px;font-size:13px;color:var(--text)}
.ct-social-link{display:inline-flex;align-items:center;gap:5px;padding:5px 11px;border-radius:7px;background:var(--bg);border:1px solid var(--border);font-size:12px;color:var(--text);text-decoration:none;transition:all .15s}
.ct-social-link:hover{border-color:var(--blue);color:var(--blue)}
.ct-tag{font-size:11px;padding:3px 9px;border-radius:99px;background:var(--bl);color:var(--blue);font-weight:600}
.ct-notes{padding:10px 13px;background:var(--bg);border-radius:8px;font-size:13px;color:var(--muted);line-height:1.6;border-left:3px solid var(--blue)}
.ct-btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;border:none;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s;min-height:36px}
.ct-btn:disabled{opacity:.55;cursor:not-allowed}
.ct-btn-primary{background:var(--blue);color:#fff}
.ct-btn-primary:hover:not(:disabled){background:var(--bd)}
.ct-btn-ghost{background:#fff;color:var(--muted);border:1.5px solid var(--border)}
.ct-btn-ghost:hover{border-color:var(--muted);color:var(--text)}
.ct-btn-red{background:#fff;color:var(--red);border:1.5px solid rgba(239,68,68,0.3)}
.ct-btn-red:hover{background:#fef2f2;border-color:var(--red)}
.ct-btn-msg{background:var(--blue);color:#fff;flex:1;justify-content:center}
.ct-flap-link{display:block;padding:9px 13px;border-radius:9px;background:var(--bl);border:1px solid rgba(28,115,186,0.2);color:var(--blue);font-size:13px;text-decoration:none;text-align:center;font-weight:600;margin-bottom:12px}
.ct-status-overlay{position:absolute;inset:0;background:rgba(255,255,255,0.92);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:20px;border-radius:14px}
::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:99px}
`;

const Ic = {
  back:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  search:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  camera:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  x:       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  star:    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  trash:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  msg:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  ext:     <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  phone:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.33 2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  email:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  map:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  globe:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  building:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="1"/><line x1="8" y1="21" x2="8" y2="3"/><line x1="16" y1="21" x2="16" y2="3"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="2" y1="15" x2="22" y2="15"/></svg>,
};

const SOURCE = {
  exchange: { label:'Exchange', color:B,         bg:'#e8f3fb' },
  scan:     { label:'Scanned',  color:'#10b981', bg:'#ecfdf5' },
  flap_user:{ label:'Flap',     color:'#7c3aed', bg:'#f5f3ff' },
  manual:   { label:'Manual',   color:'#f59e0b', bg:'#fffbeb' },
};

const PLAT_ICONS = { whatsapp:'💬', instagram:'📸', linkedin:'💼', twitter:'🐦', facebook:'📘', youtube:'▶️', tiktok:'🎵', telegram:'✈️', github:'🐙', website:'🌐' };

function Avatar({ name, photo, size = 44 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const hue = [...(name || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return photo
    ? <img src={photo} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>
    : <div style={{ width: size, height: size, borderRadius: '50%', background: `hsl(${hue},50%,42%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.35, flexShrink: 0 }}>{initials}</div>;
}

function SrcBadge({ source }) {
  const s = SOURCE[source] || SOURCE.manual;
  return <span className="ct-badge" style={{ color: s.color, background: s.bg }}>{s.label}</span>;
}

// ── Load jsQR via script injection (fixes the broken <script> in JSX) ────────
function useJsQR() {
  const [ready, setReady] = useState(!!window.jsQR);
  useEffect(() => {
    if (window.jsQR) { setReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
    script.async = true;
    script.onload = () => setReady(true);
    script.onerror = () => console.warn('[Contacts] jsQR failed to load — manual input will still work');
    document.head.appendChild(script);
    return () => {}; // leave script in DOM for reuse
  }, []);
  return ready;
}

// ── Camera Scanner modal ──────────────────────────────────────────────────────
function ScanModal({ onResult, onClose }) {
  const jsQrReady  = useJsQR();
  const videoRef   = useRef(null);
  const streamRef  = useRef(null);
  const intervalRef= useRef(null);
  const [manual, setManual] = useState('');
  const [state,  setState]  = useState('starting'); // starting | scanning | loading | done | error | no-camera
  const [msg,    setMsg]    = useState('');

  // Extract flapid from URL or raw string
  const extractFlapId = (raw) => {
    const trimmed = raw.trim();
    try {
      const url = new URL(trimmed);
      // handles /flapid or /u/flapid or /?flapid=xxx
      const parts = url.pathname.replace(/^\/+/, '').split('/').filter(Boolean);
      return parts[parts.length - 1] || trimmed;
    } catch {
      return trimmed; // already a raw flapid
    }
  };

  const stopCamera = useCallback(() => {
    clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const importFlapId = useCallback(async (raw) => {
    const flapId = extractFlapId(raw);
    if (!flapId) return;
    stopCamera();
    setState('loading');
    setMsg('Looking up profile…');
    try {
      const { data } = await api.post('/contacts/scan-import', { flapId });
      setState('done');
      setMsg(data.alreadyExists ? 'Already in your contacts ✓' : `${data.contact?.name || flapId} added!`);
      setTimeout(() => { onResult(data.contact); onClose(); }, 1500);
    } catch (err) {
      setState('error');
      setMsg(err.response?.data?.error || 'Profile not found for this card');
    }
  }, [stopCamera, onResult, onClose]);

  // Start camera
  const startCamera = useCallback(async () => {
    setState('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 } } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setState('scanning');
      setMsg('');
    } catch {
      setState('no-camera');
      setMsg('Camera not available. Enter the FlapID below.');
    }
  }, []);

  useEffect(() => { startCamera(); return () => stopCamera(); }, [startCamera, stopCamera]);

  // QR decode loop — only runs when jsQR is loaded
  useEffect(() => {
    if (state !== 'scanning' || !jsQrReady) return;
    intervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (!video || !video.videoWidth || !video.readyState >= 2) return;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = window.jsQR?.(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
      if (code?.data) {
        clearInterval(intervalRef.current);
        importFlapId(code.data);
      }
    }, 400);
    return () => clearInterval(intervalRef.current);
  }, [state, jsQrReady, importFlapId]);

  const CORNERS = [
    { top: 10, left: 10,  borderTop: '3px solid '+B, borderLeft: '3px solid '+B,  borderRadius: '4px 0 0 0' },
    { top: 10, right: 10, borderTop: '3px solid '+B, borderRight: '3px solid '+B, borderRadius: '0 4px 0 0' },
    { bottom: 10, left: 10,  borderBottom: '3px solid '+B, borderLeft: '3px solid '+B,  borderRadius: '0 0 0 4px' },
    { bottom: 10, right: 10, borderBottom: '3px solid '+B, borderRight: '3px solid '+B, borderRadius: '0 0 4px 0' },
  ];

  return (
    <div className="ct-modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal" style={{ maxWidth: 420 }}>
        <div className="ct-modal-head">
          <div className="ct-modal-title">Scan Flap card</div>
          <button className="ic-btn" onClick={() => { stopCamera(); onClose(); }}>{Ic.x}</button>
        </div>
        <div className="ct-modal-body">
          {/* Camera viewfinder */}
          <div className="ct-scanner-frame" style={{ marginBottom: 16 }}>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>

            {/* Corner brackets */}
            {CORNERS.map((style, i) => (
              <div key={i} className="ct-scanner-corner" style={{ position: 'absolute', width: 24, height: 24, ...style }}/>
            ))}

            {/* Scanning line */}
            {state === 'scanning' && <div className="ct-scanner-line"/>}

            {/* Status overlay */}
            {(state === 'loading' || state === 'done' || state === 'error' || state === 'no-camera') && (
              <div className="ct-status-overlay">
                {state === 'loading' && <span className="ct-spinner"/>}
                {state === 'done'    && <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                {state === 'error'   && <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
                {state === 'no-camera' && <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>}
                <div style={{ fontSize: 13, color: '#374151', textAlign: 'center', fontWeight: 500 }}>{msg}</div>
                {state === 'error' && (
                  <button className="ct-btn ct-btn-ghost" style={{ marginTop: 4 }} onClick={() => { setState('scanning'); startCamera(); }}>Try again</button>
                )}
              </div>
            )}

            {!jsQrReady && state === 'scanning' && (
              <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center' }}>
                <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.85)', padding: '3px 10px', borderRadius: 99, color: '#6b7280' }}>Loading QR decoder…</span>
              </div>
            )}
          </div>

          {/* Manual input */}
          <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
            <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Or enter manually</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="ct-input" style={{ flex: 1 }} value={manual} onChange={e => setManual(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && importFlapId(manual)}
                placeholder="FlapID, username, or profile URL"/>
              <button className="ct-btn ct-btn-primary" disabled={!manual.trim()} onClick={() => importFlapId(manual)}>Import</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Contact detail modal ──────────────────────────────────────────────────────
function ContactDetail({ contact, onClose, onUpdate, onDelete, onMessage }) {
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({ notes: contact.notes || '', tags: (contact.tags || []).join(', ') });
  const [saving,  setSaving]  = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/contacts/${contact._id}`, {
        notes: form.notes,
        tags:  form.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      onUpdate(data.contact); setEditing(false);
    } catch {}
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm(`Remove ${contact.name} from contacts?`)) return;
    await api.delete(`/contacts/${contact._id}`);
    onDelete(contact._id); onClose();
  };

  const toggleFav = async () => {
    const { data } = await api.put(`/contacts/${contact._id}`, { isFavorite: !contact.isFavorite });
    onUpdate(data.contact);
  };

  const infoRows = [
    contact.phone        && [Ic.phone,   contact.phone,        `tel:${contact.phone}`],
    contact.email        && [Ic.email,   contact.email,        `mailto:${contact.email}`],
    (contact.jobtitle || contact.organization) && [Ic.building, [contact.jobtitle, contact.organization].filter(Boolean).join(' at '), null],
    contact.address      && [Ic.map,     contact.address,      null],
    contact.website_url  && [Ic.globe,   contact.website_url,  contact.website_url],
  ].filter(Boolean);

  return (
    <div className="ct-modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal">
        <div className="ct-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <Avatar name={contact.name} photo={contact.photo} size={48}/>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.name}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                <SrcBadge source={contact.source}/>
                {contact.isFavorite && <span className="ct-badge" style={{ color: '#b45309', background: '#fffbeb' }}>★ Fav</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="ic-btn" onClick={toggleFav} title={contact.isFavorite ? 'Remove favourite' : 'Add favourite'}
              style={{ color: contact.isFavorite ? '#f59e0b' : undefined }}>{Ic.star}</button>
            <button className="ic-btn" onClick={onClose}>{Ic.x}</button>
          </div>
        </div>

        <div className="ct-modal-body">
          {/* Info rows */}
          {infoRows.map(([icon, val, href], i) => (
            <div key={i} className="ct-row">
              <span style={{ color: '#9ca3af', flexShrink: 0, marginTop: 1 }}>{icon}</span>
              {href
                ? <a href={href} style={{ color: B, textDecoration: 'none', fontSize: 13 }}>{val}</a>
                : <span style={{ fontSize: 13 }}>{val}</span>
              }
            </div>
          ))}

          {/* Social links */}
          {contact.socialLinks?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {contact.socialLinks.map((sl, i) => (
                <a key={i} href={sl.url} target="_blank" rel="noreferrer" className="ct-social-link">
                  {PLAT_ICONS[sl.platform] || Ic.globe} {sl.label || sl.platform}
                </a>
              ))}
            </div>
          )}

          {/* Tags */}
          {contact.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              {contact.tags.map(t => <span key={t} className="ct-tag">{t}</span>)}
            </div>
          )}

          {/* Notes */}
          {!editing && contact.notes && (
            <div className="ct-notes" style={{ marginBottom: 14 }}>
              <strong style={{ color: B }}>Note:</strong> {contact.notes}
            </div>
          )}

          {/* Edit form */}
          {editing && (
            <div style={{ marginBottom: 14 }}>
              <div className="ct-field">
                <label className="ct-label">Notes</label>
                <textarea className="ct-textarea" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Private notes…"/>
              </div>
              <div className="ct-field">
                <label className="ct-label">Tags (comma-separated)</label>
                <input className="ct-input" value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="work, conference, 2024"/>
              </div>
            </div>
          )}

          {/* Flap profile link */}
          {contact.flapId && (
            <a href={`/${contact.flapId}`} target="_blank" rel="noreferrer" className="ct-flap-link">
              View Flap profile {Ic.ext}
            </a>
          )}

          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 16 }}>
            Added {new Date(contact.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {contact.flapUserId && (
              <button className="ct-btn ct-btn-msg" onClick={() => { onMessage(contact); onClose(); }}>
                {Ic.msg} Message
              </button>
            )}
            {editing ? (
              <>
                <button className="ct-btn ct-btn-ghost" style={{ flex: 1 }} onClick={() => setEditing(false)}>Cancel</button>
                <button className="ct-btn ct-btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={save} disabled={saving}>
                  {saving ? <span className="ct-spinner" style={{ width: 12, height: 12 }}/> : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button className="ct-btn ct-btn-ghost" style={{ flex: 1 }} onClick={() => setEditing(true)}>Edit notes</button>
                <button className="ct-btn ct-btn-red" onClick={del}>{Ic.trash}</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Contacts() {
  const navigate = useNavigate();
  const [contacts,     setContacts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [q,            setQ]            = useState('');
  const [filter,       setFilter]       = useState('all');
  const [selected,     setSelected]     = useState(null);
  const [showScanner,  setShowScanner]  = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q && q.length >= 1) params.set('q', q);
      if (filter !== 'all') params.set('source', filter);
      const { data } = await api.get(`/contacts?${params}`);
      setContacts(data.contacts || []);
    } catch { if (!localStorage.getItem('token')) navigate('/login'); }
    setLoading(false);
  }, [q, filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = v => {
    setQ(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(load, 350);
  };

  const grouped = () => {
    const sorted = [...contacts].sort((a, b) => {
      if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    const g = {};
    sorted.forEach(c => {
      const k = c.isFavorite ? '★ Favourites' : (c.name?.[0] || '#').toUpperCase();
      (g[k] = g[k] || []).push(c);
    });
    return g;
  };

  const FILTERS = [
    { key: 'all',       label: 'All' },
    { key: 'flap_user', label: 'Flap users' },
    { key: 'exchange',  label: 'Exchanged' },
    { key: 'scan',      label: 'Scanned' },
    { key: 'manual',    label: 'Manual' },
  ];

  return (
    <div className="ct-root">
      <style>{CSS}</style>

      <nav className="ct-nav">
        <button className="ic-btn" onClick={() => navigate('/profile')}>{Ic.back}</button>
        <a href="/" className="ct-logo">flap<span>.</span></a>
        <div style={{ color: '#9ca3af', fontSize: 13 }}>{contacts.length} contacts</div>
        <button className="ct-scan-btn" onClick={() => setShowScanner(true)}>{Ic.camera} Scan card</button>
      </nav>

      <div className="ct-body">
        <div className="ct-search-wrap">
          <div className="ct-search-inner">
            <span style={{ color: '#9ca3af', flexShrink: 0 }}>{Ic.search}</span>
            <input className="ct-search" value={q} onChange={e => handleSearch(e.target.value)} placeholder="Search name, phone, company…"/>
            {q && <button className="ic-btn" style={{ border: 'none', background: 'transparent' }} onClick={() => { setQ(''); load(); }}>{Ic.x}</button>}
          </div>
          <div className="ct-filters" style={{ marginTop: 10 }}>
            {FILTERS.map(f => (
              <button key={f.key} className={`ct-filter ${filter === f.key ? 'sel' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="ct-center" style={{ paddingTop: 60 }}><span className="ct-spinner"/></div>
        )}

        {!loading && contacts.length === 0 && (
          <div className="ct-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1" style={{ marginBottom: 16 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
              {q ? `No results for "${q}"` : 'No contacts yet'}
            </div>
            <div style={{ fontSize: 13, maxWidth: 280, margin: '0 auto', lineHeight: 1.7 }}>
              {q ? 'Try a different search' : 'Scan a Flap card or exchange contacts on someone\'s profile page'}
            </div>
            {!q && (
              <button className="ct-scan-btn" style={{ marginTop: 20, padding: '10px 22px' }} onClick={() => setShowScanner(true)}>
                {Ic.camera} Scan your first card
              </button>
            )}
          </div>
        )}

        {!loading && contacts.length > 0 && Object.entries(grouped()).map(([letter, group]) => (
          <div key={letter}>
            <div className="ct-group-label">{letter} <span style={{ fontWeight: 400, color: '#d1d5db' }}>({group.length})</span></div>
            {group.map(contact => (
              <div key={contact._id} className="ct-contact" onClick={() => setSelected(contact)}>
                <Avatar name={contact.name} photo={contact.photo} size={46}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ct-contact-name">{contact.name}</div>
                  <div className="ct-contact-sub">
                    {contact.jobtitle && contact.organization
                      ? `${contact.jobtitle} · ${contact.organization}`
                      : contact.organization || contact.phone || contact.email || ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <SrcBadge source={contact.source}/>
                  {contact.isFavorite && <span style={{ color: '#f59e0b', fontSize: 14 }}>★</span>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {showScanner && (
        <ScanModal
          onResult={contact => { if (contact) setContacts(cs => [...cs.filter(c => c._id !== contact._id), contact]); }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {selected && (
        <ContactDetail
          contact={selected}
          onClose={() => setSelected(null)}
          onUpdate={updated => { setContacts(cs => cs.map(c => c._id === updated._id ? updated : c)); setSelected(updated); }}
          onDelete={id => setContacts(cs => cs.filter(c => c._id !== id))}
          onMessage={contact => navigate('/messages', { state: { openConvoWith: contact.flapUserId } })}
        />
      )}
    </div>
  );
}